import { isNumeric } from '../../util/helpers';
import { TakeoffLandArea } from './takeoff-land-area.model';
/**
 * Takeoff/land area type
 */
export var LandingSurfaceType;
(function (LandingSurfaceType) {
    LandingSurfaceType[LandingSurfaceType["Runway"] = 0] = "Runway";
    LandingSurfaceType[LandingSurfaceType["Waterway"] = 1] = "Waterway";
    LandingSurfaceType[LandingSurfaceType["Helipad"] = 2] = "Helipad";
})(LandingSurfaceType || (LandingSurfaceType = {}));
/**
 * Landing surface condition
 */
export var LandingSurfaceCondition;
(function (LandingSurfaceCondition) {
    LandingSurfaceCondition[LandingSurfaceCondition["Excellent"] = 0] = "Excellent";
    LandingSurfaceCondition[LandingSurfaceCondition["Good"] = 1] = "Good";
    LandingSurfaceCondition[LandingSurfaceCondition["Fair"] = 2] = "Fair";
    LandingSurfaceCondition[LandingSurfaceCondition["Poor"] = 3] = "Poor";
    LandingSurfaceCondition[LandingSurfaceCondition["Failed"] = 4] = "Failed";
    LandingSurfaceCondition[LandingSurfaceCondition["NotReported"] = 5] = "NotReported";
})(LandingSurfaceCondition || (LandingSurfaceCondition = {}));
/**
 * Pavement type (flexible or rigid)
 */
export var PavementType;
(function (PavementType) {
    PavementType[PavementType["Flexible"] = 0] = "Flexible";
    PavementType[PavementType["Rigid"] = 1] = "Rigid";
})(PavementType || (PavementType = {}));
/**
 * Subgrade Strength classification
 */
export var SubgradeStrength;
(function (SubgradeStrength) {
    SubgradeStrength[SubgradeStrength["UltraLow"] = 0] = "UltraLow";
    SubgradeStrength[SubgradeStrength["Low"] = 1] = "Low";
    SubgradeStrength[SubgradeStrength["Medium"] = 2] = "Medium";
    SubgradeStrength[SubgradeStrength["High"] = 3] = "High";
})(SubgradeStrength || (SubgradeStrength = {}));
/**
 * Tire pressure classification
 */
export var TirePressure;
(function (TirePressure) {
    TirePressure[TirePressure["UltraLow"] = 0] = "UltraLow";
    TirePressure[TirePressure["Low"] = 1] = "Low";
    TirePressure[TirePressure["Medium"] = 2] = "Medium";
    TirePressure[TirePressure["High"] = 3] = "High";
})(TirePressure || (TirePressure = {}));
/**
 * Edge lighting type
 */
export var EdgeLighting;
(function (EdgeLighting) {
    EdgeLighting[EdgeLighting["High"] = 0] = "High";
    EdgeLighting[EdgeLighting["Low"] = 1] = "Low";
    EdgeLighting[EdgeLighting["Strobe"] = 2] = "Strobe";
    EdgeLighting[EdgeLighting["Medium"] = 3] = "Medium";
    EdgeLighting[EdgeLighting["Flood"] = 4] = "Flood";
    EdgeLighting[EdgeLighting["Perimeter"] = 5] = "Perimeter";
    EdgeLighting[EdgeLighting["NonStandard"] = 6] = "NonStandard";
})(EdgeLighting || (EdgeLighting = {}));
/**
 * Landing surface model (e.g., runway, waterway, or helipad). For runways, there is a single LandingSurface model which
 * encompasses both ends (e.g., runway "05/23"). Each reciprocal end of the runway is modeled as a TakeoffLandArea (so
 * a LandingSurface has one or more takeoff/land areas). Helipads only have a single TakeoffLandArea.
 */
var LandingSurface = /** @class */ (function () {
    function LandingSurface() {
    }
    Object.defineProperty(LandingSurface.prototype, "paved", {
        /**
         * Returns true if the runway surface is paved
         */
        get: function () {
            return this.materialType1 === 'asphalt' || this.materialType1 === 'concrete' || this.materialType1 === 'pem';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LandingSurface.prototype, "uriFragment", {
        /**
         * Returns the runway named transformed so it can be used as part of a URL fragment (to allow linking directly
         * to the appropriate runway detail section of the page).
         * @returns {string}
         */
        get: function () {
            return 'rwy' + this.name.toUpperCase()
                .replace('/', '-')
                .replace(/[^0-9A-Z-]/g, '');
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Parses landing surface
     * @param data {any} Landing surface data as returned by API
     * @return {LandingSurface} Parsed landing surface
     */
    LandingSurface.parse = function (data) {
        return Object.assign(new LandingSurface(), {
            landingSurfaceId: data.landingSurfaceId,
            airportId: data.airportId,
            name: data.name || '',
            type: this.parseLandingSurfaceType(data.areaType),
            length: isNumeric(data.length) ? +data.length : null,
            lengthMeters: isNumeric(data.length) ? +Math.round(data.length / 3.28084) : null,
            width: isNumeric(data.width) ? +data.width : null,
            widthMeters: isNumeric(data.width) ? +Math.round(data.width / 3.28084) : null,
            materialType1: data.materialType1 || '',
            materialType2: data.materialType2 || '',
            treatment: data.treatment || '',
            condition: this.parseCondition(data.condition),
            surfaceReadable: data.surfaceReadable,
            pcn: isNumeric(data.pcn) ? +data.pcn : null,
            wtBearing1Wheel: isNumeric(data.wtBearing1Wheel) ? +data.wtBearing1Wheel : null,
            wtBearing2Wheel: isNumeric(data.wtBearing2Wheel) ? +data.wtBearing2Wheel : null,
            wtBearing2WheelTandem: isNumeric(data.wtBearing2WheelTandem) ? +data.wtBearing2WheelTandem : null,
            wtBearing2WheelDblTandem: isNumeric(data.wtBearing2WheelDblTandem) ? +data.wtBearing2WheelDblTandem : null,
            pavementType: this.parsePavementType(data.pavementType),
            subgradeStrength: this.parseSubgradeStrength(data.subgradeStrength),
            tirePressure: this.parseTirePressure(data.tirePressure),
            edgeLighting: this.parseEdgeLighting(data.edgeLighting),
            runwayedgeLights: data.edgeLighting,
            takeOffLandAreas: (data.takeOffLandAreas || []).map(function (a) { return TakeoffLandArea.parse(a); })
        });
    };
    /**
     * Parses landing surface type from string
     * @param value {string} Landing surface type as returned by API
     * @return {LandingSurfaceType} Parsed landing surface type
     */
    LandingSurface.parseLandingSurfaceType = function (value) {
        if (value != null) {
            value = value.toLowerCase();
        }
        switch (value) {
            case 'runway':
                return LandingSurfaceType.Runway;
            case 'waterway':
                return LandingSurfaceType.Waterway;
            case 'pad':
                return LandingSurfaceType.Helipad;
            default:
                return null;
        }
    };
    /**
     * Formats landing surface type for display
     * @param {LandingSurfaceType} value
     * @returns {string}
     */
    LandingSurface.formatLandingSurfaceType = function (value) {
        switch (value) {
            case LandingSurfaceType.Runway:
                return 'Runway';
            case LandingSurfaceType.Waterway:
                return 'Waterway';
            case LandingSurfaceType.Helipad:
                return 'Helipad';
            default:
                return '';
        }
    };
    /**
     * Parses landing surface condition
     * @param value {any} Landing surface condition as returned by API
     * @return {LandingSurfaceCondition} Parsed landing surface condition
     */
    LandingSurface.parseCondition = function (value) {
        if (value != null) {
            value = value.toLowerCase();
        }
        switch (value) {
            case 'good':
                return LandingSurfaceCondition.Good;
            case 'failed':
                return LandingSurfaceCondition.Failed;
            case 'poor':
                return LandingSurfaceCondition.Poor;
            case 'excellent':
                return LandingSurfaceCondition.Excellent;
            case 'fair':
                return LandingSurfaceCondition.Fair;
            case 'not reported':
                return LandingSurfaceCondition.NotReported;
            default:
                return null;
        }
    };
    /**
     * Formats landing surface condition
     * @param value {LandingSurfaceCondition} Landing sufrace condition enum
     * @return string Formatted landing surface string
     */
    LandingSurface.formatCondition = function (value) {
        switch (value) {
            case LandingSurfaceCondition.Good:
                return 'Good';
            case LandingSurfaceCondition.Excellent:
                return 'Excellent';
            case LandingSurfaceCondition.Failed:
                return 'Failed';
            case LandingSurfaceCondition.Fair:
                return 'Fair';
            case LandingSurfaceCondition.Poor:
                return 'Poor';
            case LandingSurfaceCondition.NotReported:
                return 'Not Reported';
            default:
                return null;
        }
    };
    /**
     * Parses pavement type
     * @param value {any} Pavement type as returned by API
     * @return {PavementType} Parsed pavement type
     */
    LandingSurface.parsePavementType = function (value) {
        if (value != null) {
            value = value.toLowerCase();
        }
        switch (value) {
            case 'flexible':
                return PavementType.Flexible;
            case 'rigid':
                return PavementType.Rigid;
            default:
                return null;
        }
    };
    /**
     * Formats pavement type
     * @param value {PavementType} the pavement type enum
     * @return {string} the formatted pavement type string
     */
    LandingSurface.formatPavementType = function (value) {
        switch (value) {
            case PavementType.Flexible:
                return 'Flexible';
            case PavementType.Rigid:
                return 'Rigid';
            default:
                return null;
        }
    };
    /**
     * Parses subgrade strength
     * @param value {any} Subgrade strength as returned by API
     * @return {SubgradeStrength} Parsed subgrade strength
     */
    LandingSurface.parseSubgradeStrength = function (value) {
        if (value != null) {
            value = value.toLowerCase();
        }
        switch (value) {
            case 'ultra low':
                return SubgradeStrength.UltraLow;
            case 'low':
                return SubgradeStrength.Low;
            case 'medium':
                return SubgradeStrength.Medium;
            case 'high':
                return SubgradeStrength.High;
            default:
                return null;
        }
    };
    /**
     * Parses tire pressure
     * @param value {any} Tire pressure as returned by API
     * @return {TirePressure} Parsed tire pressure
     */
    LandingSurface.parseTirePressure = function (value) {
        if (value != null) {
            value = value.toLowerCase();
        }
        switch (value) {
            case 'ultra low':
                return TirePressure.UltraLow;
            case 'low':
                return TirePressure.Low;
            case 'medium':
                return TirePressure.Medium;
            case 'high':
                return TirePressure.High;
            default:
                return null;
        }
    };
    /**
     * Parses edge lighting type
     * @param value {any} Edge lighting type as returned by API
     * @return {TirePressure} Parsed edge lighting
     */
    LandingSurface.parseEdgeLighting = function (value) {
        if (value != null) {
            value = value.toLowerCase();
        }
        switch (value) {
            case 'high':
                return EdgeLighting.High;
            case 'low':
                return EdgeLighting.Low;
            case 'strobe':
                return EdgeLighting.Strobe;
            case 'medium':
                return EdgeLighting.Medium;
            case 'flood':
                return EdgeLighting.Flood;
            case 'perimeter':
                return EdgeLighting.Perimeter;
            case 'non-std':
                return EdgeLighting.NonStandard;
            default:
                return null;
        }
    };
    /**
     * Formats edge lighting for display
     */
    LandingSurface.formatEdgeLighting = function (value) {
        switch (value) {
            case EdgeLighting.High:
                return 'High';
            case EdgeLighting.Low:
                return 'Low';
            case EdgeLighting.Strobe:
                return 'Strobe';
            case EdgeLighting.Medium:
                return 'Medium';
            case EdgeLighting.Flood:
                return 'Flood';
            case EdgeLighting.Perimeter:
                return 'Perimeter';
            case EdgeLighting.NonStandard:
                return 'Non-standard';
            default:
                return null;
        }
    };
    return LandingSurface;
}());
export { LandingSurface };
